import React, { useCallback, useState } from 'react';
import { Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import classNames from 'classnames';

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    justifyContent: 'center',
    maxWidth: '100%',
    overflow: 'hidden',
    position: 'relative',
    width: '100%',
  },
  image: {
    height: '100%',
  },
}));

export type ImageViewerProps = React.PropsWithChildren<{
  backgroundColor?: string;
  className?: string;
  downloadable?: boolean;
  name: string | undefined;
  height: number;
  onClick?: () => void;
  onDownload?: () => void;
  onLoad?: () => void;
  url: string;
  width: number;
}>;

const ImageViewer = React.forwardRef<HTMLDivElement, ImageViewerProps>(
  (props: ImageViewerProps, ref): JSX.Element => {
    const classes = useStyles();

    const {
      backgroundColor,
      children,
      className,
      downloadable = false,
      height,
      name,
      onClick,
      onLoad,
      url,
      width,
    } = props;

    const [ready, setReady] = useState(false);

    const onImageLoaded = useCallback(() => {
      setReady(true);
      if (onLoad) {
        onLoad();
      }
    }, [onLoad]);

    let colorPlaceholder = backgroundColor ?? 'var(--background-color-gray)';
    if (ready) {
      colorPlaceholder = 'transparent';
    }

    return (
      <Box
        {...{ ref }}
        className={classNames(classes.container, className)}
        onClick={onClick}
        style={{
          backgroundColor: colorPlaceholder,
          maxWidth: width,
          // padding bottom + absolute positioned image allows for a pretty neat
          // loading effect when we have a predetermined background color
          paddingBottom: `min(${(height / width) * 100}%, ${height}px, 60vh)`,
        }}
      >
        <img
          alt={name}
          className={classes.image}
          src={url}
          style={{
            pointerEvents: downloadable ? 'initial' : 'none',
            position: 'absolute',
          }}
          onLoad={onImageLoaded}
        />
        {children}
      </Box>
    );
  }
);

export default ImageViewer;
